import i18n from "@/i18n";

const calculateTotalPrice = (impressions, cpm) => {
    if (!impressions || !cpm) return 0;
    return (impressions / 1000) * cpm;
}


/**
 * A price with its currency symbol. Amounts are shown with cents, except where
 * they only add noise: a tier is sold for a round sum the publisher types in,
 * so trimWholeCents renders it as EUR 300 while still showing the cents of an
 * amount that actually has them.
 */
/**
 * What the client is billed for a campaign. A tier is sold for a flat sum that
 * already covers both halves of the delivery, so its price is the price; an
 * Audience Network package is billed per thousand impressions it serves, which
 * is only the share of the goal that runs through the network.
 */
const calculateCampaignPrice = (campaign) => {
    const selectedPackage = campaign?.package;

    if (!selectedPackage) return 0;

    if (selectedPackage.is_tier) {
        return Number(selectedPackage.flat_price || 0);
    }

    return calculateTotalPrice(
        campaign.smart_ads_programatic_buying_impressions,
        (campaign.programmatic_buying_ads_percentage_distribution / 100) * (selectedPackage.publisher_cpm || 0)
    );
}

const formatPrice = (price, currency = 'USD', { locale = 'en-US', trimWholeCents = false } = {}) => {
    const priceFormatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: trimWholeCents && Number(price) % 1 === 0 ? 0 : 2,
    });

    return priceFormatter.format(price);
}

/**
 * A count with its thousands grouped the way the reader's language groups them:
 * 20,000 in English, 20.000 in German.
 *
 * The language comes from i18next rather than the browser, so the number matches the
 * words printed next to it -- a German page on an English browser was showing German
 * labels around English-grouped figures. Fractions are dropped because every caller
 * counts whole things (impressions, minutes, visitors), and a share of a goal such as
 * 1000 x 0.33 would otherwise print its floating-point tail.
 */
const numSeparator = (num) => Number(num || 0).toLocaleString(
    i18n.language || 'en',
    { maximumFractionDigits: 0 }
);

const getSiteName = (url) => {
    const cleanUrl = decodeURIComponent(url).trim();

    const hostname = new URL(
        cleanUrl.startsWith("http") ? cleanUrl : `https://${cleanUrl}`
    ).hostname;

    const name = hostname.replace(/^www\./, "").split(".")[0];

    const words = name.split("-");

    if (words.length === 1 && words[0].length <= 4) {
        return words[0].toUpperCase();
    }

    return words
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

// Google's public favicon endpoint — no crawling/scraping needed, works for any domain.
const getFaviconUrl = (url, size = 64) => {
    const cleanUrl = decodeURIComponent(url).trim();

    const hostname = new URL(
        cleanUrl.startsWith("http") ? cleanUrl : `https://${cleanUrl}`
    ).hostname;

    return `https://www.google.com/s2/favicons?domain=${hostname}&sz=${size}`;
};

/**
 * AD-208: the step that has to be completed before the given one and is not, or null
 * when the booking may be on it.
 *
 * Delivery creates the campaign row that Targeting reads its split from, so Targeting
 * shows neither of its sections without it. An unsaved campaign comes back with no id,
 * which is what makes this a reliable test.
 */
const getBlockingStep = (step, campaign) => (
    step === 'targeting' && !campaign?.id ? 'delivery' : null
);

const getIsDeliveryLocked = (campaign) => Boolean(
    campaign?.delivery_locked
    ?? campaign?.has_started_delivering
    ?? (campaign?.is_launched || campaign?.internal_status === 'published')
);

export { calculateTotalPrice, calculateCampaignPrice, formatPrice, numSeparator, getSiteName, getFaviconUrl, getIsDeliveryLocked, getBlockingStep };